"use client";

import { Theme as RadixThemes } from "@radix-ui/themes";
import type { GetPropDefTypes, themePropDefs } from "@radix-ui/themes/props";
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import cx from "clsx";
import * as React from "react";
import { namespaceClassNames } from "./lib/utils.js";
import { ExpiredTokenError, IncorrectPermissionsError } from "./lib/errors.js";
import { DEFAULT_API_HOSTNAME } from "./lib/api/config.js";
import type { Elements } from "./lib/elements.js";
import { WidgetsContext } from "./lib/widgets-context.js";
import { WIDGETS_DATA_ATTRIBUTE_NAMESPACE } from "./lib/constants.js";

export interface WorkOsWidgetsProps {
  elements?: Elements;
  theme?: Omit<GetPropDefTypes<typeof themePropDefs>, "asChild"> & {
    fontFamily?: string;
  };
  style?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
  apiHostname?: string;
  port?: number | null;
  https?: boolean;
  /**
   * Optional query client for users who are already using Tanstack Query. If
   * not provided, a new query client will be created and mounted internally.
   */
  queryClient?: QueryClient;
}

export const WorkOsWidgets: React.FC<WorkOsWidgetsProps> = ({
  elements = {},
  className,
  theme: { fontFamily, ...radixThemesProps } = {},
  style = {},
  children,
  apiHostname = DEFAULT_API_HOSTNAME,
  port = null,
  https = true,
  queryClient,
}) => {
  const customStyle: React.CSSProperties = fontFamily
    ? {
        "--default-font-family": fontFamily,
        ...style,
      }
    : style;

  const apiBaseUrl = React.useMemo(() => {
    try {
      const url = new URL(
        "/",
        `${https ? "https" : "http"}://${apiHostname}${port ? `:${port}` : ""}`,
      );
      return url.toString().slice(0, -1);
    } catch {
      console.error(
        "Failed to validate the WorkOS API URL. Check to ensure that valid `apiHostname` and `port` props are passed to WorkOsWidgets",
      );
      return `https://${DEFAULT_API_HOSTNAME}`;
    }
  }, [apiHostname, https, port]);

  return (
    <QueryClientProviderImpl client={queryClient}>
      <RadixThemes
        {...radixThemesProps}
        {...{ [`data-${WIDGETS_DATA_ATTRIBUTE_NAMESPACE}-root`]: true }}
        style={customStyle}
        className={cx(namespaceClassNames("root"), className)}
      >
        <WidgetsContext.Provider value={{ elements, apiBaseUrl }}>
          {children}
        </WidgetsContext.Provider>
      </RadixThemes>
    </QueryClientProviderImpl>
  );
};

const QueryClientProviderImpl = ({
  client: providedClient,
  children,
}: {
  client: QueryClient | undefined;
  children: React.ReactNode;
}) => {
  const [queryClient] = React.useState(
    () =>
      providedClient ??
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (
                error instanceof IncorrectPermissionsError ||
                error instanceof ExpiredTokenError
              ) {
                return false;
              }
              return failureCount < 3;
            },
          },
        },
      }),
  );

  const client = useQueryClient(queryClient);
  if (Object.is(client, providedClient)) {
    // No need for a new context provider if the user provided the client to
    // their own provider, which has already mounted the client.
    return <>{children}</>;
  }

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
};
